import { isUndefined, noop } from 'lodash/fp';
import React, { useCallback, useMemo, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { useWindowResize } from '../../../../hooks/useWindowResize';
import { capitaliseStartLetter } from '../../../../utils/regex-utils';
import { ReadOnlyCalculatorPadOption } from '../../../shared/calculator/ReadOnlyCalculatorPadOption';
import { DragDrop } from '../../../shared/drag-n-drop/DragDrop';
import { DraggableItem } from '../../../shared/drag-n-drop/shared';
import { Icon } from '../../../shared/icon/Icon';
import { CaretDown, CaretSide } from '../../../shared/icons';
import { LongText } from '../../../shared/longtext/LongText';
import { Scrollable } from '../../../shared/scrollable/Scrollable';
import { Text } from '../../../shared/text/Text';
import { Toggle } from '../../../shared/toggle';
import { WizardTab } from '../../../shared/wizard/WizardTab';
import { CalculatorFieldTypeUI, CalculatorOptionType, CalculatorPadOptionDB, CalculatorPadOptionUI, getAllPadOptions, updateFieldTypeValue } from '../store';
import styles from './CalculatorManager.module.scss';

const { amber, red, primary } = styles;

interface SelectedCalculatorFieldTypeProps {
    calculatorField: CalculatorFieldTypeUI;
    nameAlreadyExists: boolean;
}

export const SelectedCalculatorFieldType: React.FC<SelectedCalculatorFieldTypeProps> = ({ calculatorField, nameAlreadyExists }) => {
    const [openSections, setOpenSections] = useState<(keyof CalculatorFieldTypeUI)[]>([]);
    const [selectedInputBuilder, setSelectedInputBuilder] = useState<keyof CalculatorFieldTypeUI | null>(null);

    const { name, description, buildPadOptions, outputPadOptions, gracePeriodPadOptions, includeOutput, includeGracePeriod, includeOther, includeConditionals, includeIfElse } = calculatorField;
    const dispatch = useAppDispatch();
    const allPadOptions = useAppSelector(getAllPadOptions);
    const availablePadOptions = useMemo(() => includeOther === 1 ? allPadOptions : allPadOptions.filter(({ type }) => type !== CalculatorOptionType.TEXT), [includeOther, allPadOptions]);

    const [screenWidth] = useWindowResize();

    const nameValue = useMemo(() => !isUndefined(calculatorField.calculatorFieldTypeId) ? capitaliseStartLetter(name) : name, [calculatorField.calculatorFieldTypeId, name]);

    const updateProperty = useCallback((key: keyof CalculatorFieldTypeUI, value: string | number | number[]) => dispatch(updateFieldTypeValue(key, value)), [dispatch]);
    const updateToggleValue = useCallback((key: keyof CalculatorFieldTypeUI, value: boolean) => updateProperty(key, value ? 1 : 0), [updateProperty]);

    const updateBuildOptionValue = useCallback((key: keyof CalculatorFieldTypeUI, options: number[], value: CalculatorPadOptionUI | CalculatorPadOptionDB) => {
        const valueToggled = value.calculatorPadOptionId;
        if (valueToggled) {
            const included = options.includes(valueToggled);
            if (included) {
                const updatedOptions = options.filter(padOptionId => padOptionId !== valueToggled);
                updateProperty(key, updatedOptions);
            } else {
                const updatedOptions = [...options, valueToggled];
                updateProperty(key, updatedOptions);
            }
        }
    }, [updateProperty]);

    const getSectionOpen = useCallback((inputBuilder: keyof CalculatorFieldTypeUI) => openSections.includes(inputBuilder), [openSections]);
    const getSectionOpenIcon = useCallback((isOpen: boolean) => isOpen ? CaretDown : CaretSide, []);
    const toggleSection = useCallback((inputBuilder: keyof CalculatorFieldTypeUI) => {
        const isOpen = getSectionOpen(inputBuilder);
        if (isOpen) {
            return setOpenSections(openSections => openSections.filter(type => type !== inputBuilder));
        }
        setSelectedInputBuilder(inputBuilder);
        return setOpenSections(openSections => ([...openSections, inputBuilder]));
    }, [getSectionOpen, setOpenSections, setSelectedInputBuilder]);

    const inputBuilderOptions: { key: keyof CalculatorFieldTypeUI; label: string; options: number[]; }[] = useMemo(() => [
        { key: 'buildPadOptions', label: 'Builder Options', options: buildPadOptions },
        { key: 'outputPadOptions', label: 'Output Options', options: outputPadOptions },
        { key: 'gracePeriodPadOptions', label: 'Grace Period Options', options: gracePeriodPadOptions }
    ], [buildPadOptions, outputPadOptions, gracePeriodPadOptions]);

    const selectedReorderPadOptionIds = useMemo(() => {
        switch (selectedInputBuilder) {
            case 'buildPadOptions':
                return buildPadOptions;
            case 'outputPadOptions':
                return outputPadOptions;
            case 'gracePeriodPadOptions':
                return gracePeriodPadOptions;
            default:
                return [];
        }
    }, [selectedInputBuilder, buildPadOptions, outputPadOptions, gracePeriodPadOptions]);

    const selectedReorderPadOptions = useMemo(() => selectedReorderPadOptionIds.map(id => allPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId === id)!), [allPadOptions, selectedReorderPadOptionIds]);

    const listOptions = useMemo(() => selectedReorderPadOptions.map(({ calculatorPadOptionId, label }) => ({ id: calculatorPadOptionId.toString(), label })), [selectedReorderPadOptions]);

    const updateList = useCallback((list: DraggableItem[]) => {
        const newFields = list.map(({ id }) => parseInt(id));
        updateProperty(selectedInputBuilder!, newFields);
    }, [updateProperty, selectedInputBuilder]);

    const reorderInputWidth = useMemo(() => `${(screenWidth * 0.1) - 10}px`, [screenWidth]);

    const getChildElement = useCallback((childId: string) => {
        const padOption = selectedReorderPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId.toString() === childId);
        if (padOption) {
            return (
                <ReadOnlyCalculatorPadOption
                    calculatorPadOption={padOption}
                    selected
                    select={noop}
                    key={padOption.calculatorPadOptionId}
                    inputWrapperWidth={reorderInputWidth}
                />
            );
        }
        return null;
    }, [selectedReorderPadOptions, reorderInputWidth]);

    const getDraggingElement = useCallback((childId: string) => {
        const padOption = selectedReorderPadOptions.find(({ calculatorPadOptionId }) => calculatorPadOptionId.toString() === childId)!;
        return (
            <ReadOnlyCalculatorPadOption
                calculatorPadOption={padOption}
                selected={false}
                select={noop}
                key={padOption.calculatorPadOptionId}
                inputWrapperWidth={reorderInputWidth}
            />
        );
    }, [selectedReorderPadOptions, reorderInputWidth]);

    return (
        <div className={styles.selectedFieldTypeWrapper}>
            <div className={styles.configureWrapper}>
                <Scrollable>
                    <Text
                        label='Name'
                        testId='selected-calculator-field-type-name'
                        onChange={e => updateProperty('name', e.target.value)}
                        value={nameValue}
                        placeholder='Name...'
                        disabled={!isUndefined(calculatorField.calculatorFieldTypeId)}
                        borderColour={nameAlreadyExists ? red : primary}
                    />
                    <LongText
                        label='Description'
                        testId='selected-calculator-field-type-description'
                        onChange={value => updateProperty('description', value)}
                        value={description}
                        placeholder='Description...'
                        width='100%'
                    />
                    {inputBuilderOptions.map(({ key, label, options }) => {
                        const sectionOpen = getSectionOpen(key);
                        return (
                            <div className={styles.inputBuilderWrapper} key={key}>
                                <div className={styles.inputBuilderHeader} onClick={() => toggleSection(key)}>
                                    <div className={styles.sectionOpenIcon}>
                                        <Icon icon={getSectionOpenIcon(sectionOpen)} fontSize={15} />
                                    </div>
                                    <div className={styles.inputBuilderTitle}>{capitaliseStartLetter(label)} ({options.length} selected)</div>
                                </div>
                                {sectionOpen &&
                                    <div className={styles.inputItemsWrapper}>
                                        {availablePadOptions.map((input, index) => (
                                            <ReadOnlyCalculatorPadOption
                                                calculatorPadOption={input}
                                                selected={options.includes(input.calculatorPadOptionId)}
                                                select={value => updateBuildOptionValue(key, options, value)}
                                                key={index}
                                                inputWrapperWidth='calc(25% - 4px)'
                                            />
                                        ))}
                                    </div>
                                }
                            </div>
                        );
                    })}
                    <div className={styles.calculatorToggleWrapper}>
                        <div className={styles.calculatorToggleTitle}>Show Output (THEN)</div>
                        <Toggle
                            checked={includeOutput === 1}
                            onChange={value => updateToggleValue('includeOutput', value)}
                            testId='calculator-field-output'
                        />
                    </div>
                    <div className={styles.calculatorToggleWrapper}>
                        <div className={styles.calculatorToggleTitle}>Show Grace Period</div>
                        <Toggle
                            checked={includeGracePeriod === 1}
                            onChange={value => updateToggleValue('includeGracePeriod', value)}
                            testId='calculator-field-grace-period'
                        />
                    </div>
                    <div className={styles.calculatorToggleWrapper}>
                        <div className={styles.calculatorToggleTitle}>Include Free Text Option</div>
                        <Toggle
                            checked={includeOther === 1}
                            onChange={value => updateToggleValue('includeOther', value)}
                            testId='calculator-field-free-text-option'
                        />
                    </div>
                    <div className={styles.calculatorToggleWrapper}>
                        <div className={styles.calculatorToggleTitle}>Include Conditionals (AND/OR)</div>
                        <Toggle
                            checked={includeConditionals === 1}
                            onChange={value => updateToggleValue('includeConditionals', value)}
                            testId='calculator-field-conditionals'
                        />
                    </div>
                    <div className={styles.calculatorToggleWrapper}>
                        <div className={styles.calculatorToggleTitle}>Include IF ELSE Statements</div>
                        <Toggle
                            checked={includeIfElse === 1}
                            onChange={value => updateToggleValue('includeIfElse', value)}
                            testId='calculator-field-if-else'
                        />
                    </div>
                </Scrollable>
            </div>
            <div className={styles.reorderWrapper}>
                <div className={styles.reorderHeader}>Prioritise Calculator Inputs</div>
                <div className={styles.selectInputBuilderWrapper}>
                    {inputBuilderOptions.map(({ key, label }) => <WizardTab label={label} key={key} onClick={() => setSelectedInputBuilder(key)} selected={selectedInputBuilder === key} />)}
                </div>
                <div className={styles.droppableReorderWrapper}>
                    <DragDrop
                        getChildElement={getChildElement}
                        list={listOptions}
                        listId='document-reporting'
                        updateList={updateList}
                        type='wrap'
                        getDraggingElement={getDraggingElement}
                        edgeColour={amber}
                        edgeWidth={3}
                    />
                </div>
            </div>
        </div>
    );
};
